import { ConfigProvider } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import { lazy, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';
import { getThemeMode } from '../../store/Theme/ThemeSlice';
import { loadLanguage } from '../../store/Language/LanguageSlice';
import Loading from '../Loading/Loading';
import { hasPermission } from '../../utilities/authentication';
import { getAntdTheme } from '../../theme/palette';
import PrivateRoute from '../Routes/PrivateRoute';

// Module-scope lazy imports — declaring these inside the component recreates
// component types on every render and remounts the layout shell.
const Forbidden = lazy(() => import('../../pages/Backoffice/Forbidden'));
const BackofficeDashboard = lazy(() => import('../../pages/Backoffice/Dashboard'));
const BackofficeVendors = lazy(() => import('../../pages/Backoffice/Vendors'));
const BackofficeVendorForm = lazy(() => import('../../pages/Backoffice/VendorForm'));
const BackofficeVendorEdit = lazy(() => import('../../pages/Backoffice/VendorEdit'));
const BackofficeSubscriptionPlans = lazy(() => import('../../pages/Backoffice/SubscriptionPlans'));
const BackofficeSubscriptions = lazy(() => import('../../pages/Backoffice/Subscriptions'));
const BackofficeSubscriptionPlanRequests = lazy(() => import('../../pages/Backoffice/SubscriptionPlanRequests'));
const BackofficeDemoRequests = lazy(() => import('../../pages/Backoffice/DemoRequests'));
const BackofficeSupportInbox = lazy(() => import('../../pages/Backoffice/SupportInbox'));
const PlatformClients = lazy(() => import('../../pages/Backoffice/PlatformClients'));
const PlatformOrders = lazy(() => import('../../pages/Backoffice/PlatformOrders'));
const PlatformInvoices = lazy(() => import('../../pages/Backoffice/PlatformInvoices'));
const PlatformUsers = lazy(() => import('../../pages/Backoffice/PlatformUsers'));
const Profile = lazy(() => import('../../pages/Profile/Profile'));
const Activities = lazy(() => import('../../pages/Activities/Activities'));

const RequirePermission = ({ permission, children }) => {
  if (permission && !hasPermission(permission)) {
    return (
      <Suspense fallback={<Loading />}>
        <Forbidden />
      </Suspense>
    );
  }
  return children;
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const { theme } = useSelector((state) => state.theme);
  const { language } = useSelector((state) => state.language);

  const cairoFont = "'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Ubuntu', sans-serif";
  const antdTheme = getAntdTheme(theme, {
    fontFamily: language === 'ar' ? cairoFont : undefined,
  });

  useEffect(() => {
    dispatch(getThemeMode());
    dispatch(loadLanguage());
  }, [dispatch]);

  return (
    <Routes>
      <Route
        path="/"
        element={
          <ConfigProvider locale={language === 'ar' ? arEG : enUS} direction={language === 'ar' ? 'rtl' : 'ltr'} theme={antdTheme}>
            <PrivateRoute />
          </ConfigProvider>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<BackofficeDashboard />} />
        <Route path="vendors" element={<BackofficeVendors />} />
        <Route path="vendors/create" element={<BackofficeVendorForm />} />
        <Route path="vendors/:id/edit" element={<BackofficeVendorEdit />} />
        <Route path="subscription-plans" element={<BackofficeSubscriptionPlans />} />
        <Route path="subscriptions" element={<BackofficeSubscriptions />} />
        <Route path="plan-requests" element={<BackofficeSubscriptionPlanRequests />} />
        <Route path="demo-requests" element={<BackofficeDemoRequests />} />
        <Route path="support" element={<BackofficeSupportInbox />} />
        <Route path="clients" element={<PlatformClients />} />
        <Route path="orders" element={<PlatformOrders />} />
        <Route path="invoices" element={<PlatformInvoices />} />
        <Route path="platform/users" element={<PlatformUsers />} />
        <Route path="profile" element={<Profile />} />
        <Route
          path="activities"
          element={
            <RequirePermission permission="view_activity_logs">
              <Activities />
            </RequirePermission>
          }
        />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
